import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DatasetType } from 'src/app/services/models/datasets.models';
import { FilterBar, FilterBarItem, FilterBarItemTypes } from 'src/app/services/models/filter-bar.model';
import { FilterSelectionTypes } from 'src/app/services/models/filter.model';
import { DynamicTableEntriesResponse } from 'src/app/services/models/dynamic-table.models';
import { TreeNode } from 'primeng/api';

@Component({
  selector: 'bhub-filter-bar',
  standalone: false,
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss'
})
export class FilterBarComponent implements OnInit {
  @Input() filterBar!: FilterBar;
  @Input() isEditing: boolean = false;

  @Output() selectedFiltersChanged = new EventEmitter<any>();

  public filterBarItemTypes = Object.values(FilterBarItemTypes);
  public filterSelectionTypes = Object.values(FilterSelectionTypes);
  public datasets: Dataset[];

  public showFilterDropdown: boolean = false;
  public selectedItemOrdinal: number = -1;
  public selectedFilter: FilterBarItem | null = null;

  public fieldNamesFromResponseMap: {[datasetId: number]: string[]} = {};
  public responseMap: {[filterName: string]: any[]} = {};
  public primaryKeyMap: {[filterName: string]: string} = {};
  public treeMap: {[filterName: string]: TreeNode[]} = {};
  public selectedTreeNodeMap: {[filterName: string]: TreeNode[]} = {};

  dropdownWidth: string = '300px'; // Default to 300px

  constructor(
    private el: ElementRef,
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService
  ) {}

  ngOnInit(): void {
    // Initialize filter bar items if not already set

    this.datasetService.getDatasetReferences().subscribe((datasets: Map<number, Dataset>) => {
      this.datasets = Array.from(datasets.values()).filter((dataset) => dataset.datasetReferenceTable.datasetType === DatasetType.Reference || dataset.datasetReferenceTable.datasetType === DatasetType.View);

      this.filterBar.filterBarItems.forEach((item) => {
        this.populateFilterSelectionMap(item);
      });
    });
  }

  ngAfterViewInit() {
    // Adjust dropdown width based on the number of filter items
    this.adjustWidth();
  }

  adjustWidth() {
    // Calculate width based on filter items length
    const baseWidth = 300; // The minimum width
    const itemWidth = 60;  // Approximate width per item (can be adjusted)
    const calculatedWidth = baseWidth + (this.filterBar.filterBarItems.length * itemWidth);

    // Set the width of the dropdown dynamically
    this.dropdownWidth = `${Math.max(baseWidth, calculatedWidth)}px`;
  }

  // Detect clicks outside the dropdown
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if(this.showFilterDropdown){
      // If the click is outside of the dropdown, close it
      const clickedInside = this.el.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closeDropdown();
      }
    }
  }

  // Close the dropdown
  closeDropdown() {
    var selectedFilters: any = {};
    this.filterBar.filterBarItems.forEach((filterBarItem: FilterBarItem) => {
      var filtersForThisItem = this.selectedTreeNodeMap[filterBarItem.filterName];
      var primaryKeyFieldName = this.primaryKeyMap[filterBarItem.filterName];
      selectedFilters[filterBarItem.datasetId] = filtersForThisItem.map((node: TreeNode) => node.data[primaryKeyFieldName]);
    });
    //selectedFilters = [datasetId] = lsit of values from primaryKeyField of dataset for that reference table
    this.selectedFiltersChanged.emit(selectedFilters);
    this.selectedItemOrdinal = -1;
    this.selectedFilter = null;
    this.showFilterDropdown = false;
  }

  onFilterItemClick(ordinal: number, filterBarItem: FilterBarItem) {
    this.selectedItemOrdinal = ordinal;
    this.selectedFilter = this.selectedItemOrdinal !== -1 ? filterBarItem : null;
    this.showFilterDropdown = true;
  }


  populateFilterSelectionMap(filterBarItem: FilterBarItem): void {
    const dataset = this.getDataset(filterBarItem.datasetId);
    if (dataset) {
      this.dynamicTableService.selectEntries(dataset.datasetReferenceTable.datasetId, 0)
      .subscribe((entries: DynamicTableEntriesResponse) => {
        const rowData = entries.rowData;

        // Get all field names from the first row
        const fieldNames = rowData.length > 0 ? Object.keys(rowData[0]) : [];

        // Get the options based on fieldNameToDisplay
        // const displayField = filterBarItem.fieldNameToDisplay;

        //store field name options
        this.fieldNamesFromResponseMap[filterBarItem.datasetId] = fieldNames; //rowData.map(row => row[displayField]);

        // Store responses
        this.responseMap[filterBarItem.filterName] = rowData;
        this.primaryKeyMap[filterBarItem.filterName] = entries.primaryKeyFieldName;
        this.treeMap[filterBarItem.filterName] = this.buildTreeNodes(rowData, filterBarItem, entries.primaryKeyFieldName);
        this.selectedTreeNodeMap[filterBarItem.filterName] = [];
      });
    }
  }

  buildTreeNodes(rowData: any[], filterBarItem: FilterBarItem, primaryKeyFieldName: string): TreeNode[] {
    if (!rowData || !Array.isArray(rowData)) return [];

    // Get dynamic field names
    const labelField = filterBarItem.fieldNameToDisplay;
    //try without idField for now
    // const idField = Object.keys(data[0]).find(k => k.toLowerCase().includes('id')) || 'id';
    const parentKeyField = 'ParentKey'; // Assuming if tree, it will always use this

    const idToNodeMap = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    // Build nodes with labelField as key
    for (const item of rowData) {
      const key = String(item[primaryKeyFieldName]);

      const node: TreeNode = {
        key: key,
        label: item[labelField],
        data: item,
        children: []
      };
      idToNodeMap.set(key, node);
    }

    // Build tree
    for (const item of rowData) {
      const key = String(item[primaryKeyFieldName]);
      const parentKey = String(item[parentKeyField]);
      if (parentKey && idToNodeMap.has(parentKey)) {
        const parentNode = idToNodeMap.get(parentKey)!;
        parentNode.children!.push(idToNodeMap.get(key)!);
      } else {
        roots.push(idToNodeMap.get(key)!);
      }
    }

    // Mark leaves
    idToNodeMap.forEach((node) => {
      if (!node.children || node.children.length === 0) {
        node.leaf = true;
      }
    });

    return roots;
  }

  getDataset(datasetId: number): Dataset | undefined {
    return this.datasets.find(ds => ds.datasetReferenceTable.datasetId === datasetId);
  }

  // getDatasetOptions(datasetId: number): string[] {
  //   var selection = [];
  //   selection.push(this.datasets.find(ds => ds.datasetReferenceTable.datasetId === datasetId)?.datasetReferenceTable.datasetName);
  //   return selection;
  // }

  // getDatasetName(datasetId: number): string {
  //   return this.datasets.find(ds => ds.datasetReferenceTable.datasetId === datasetId)?.datasetReferenceTable.datasetName ?? 'Unknown Dataset';
  // }

  onDatasetChange(filterBarItem: FilterBarItem) {
    this.populateFilterSelectionMap(filterBarItem);
    filterBarItem.fieldNameToDisplay = '';
    // this.filterBarChanged.emit(filterBarItem);
  }

  onFieldNameToDisplayChange(filterBarItem: FilterBarItem) {
    // this.filterBarChanged.emit(filterBarItem);
  }

  addFilterGroup(): void {
    const newItem: FilterBarItem = {
      ordinal: this.filterBar.filterBarItems.length + 1,
      filterName: '',
      fieldNameToDisplay: '',
      filterBarItemType: FilterBarItemTypes.Dataset, // or whatever default you want
      filterSelectionType: FilterSelectionTypes.Multiple, // or whatever default you want
      datasetId: 0, // Set this based on available datasets
      filterRuleIds: []
    };

    this.filterBar.filterBarItems.push(newItem);
  }

  onDeleteFilterBarItem(tempRow: FilterBarItem): void {
    const index = this.filterBar.filterBarItems.indexOf(tempRow);
    if (index > -1) {
      this.filterBar.filterBarItems.splice(index, 1);
    }
  }
}
